// Merchant MIS Column Config – Report Configuration (Control Center)
// Uses API from apiUrls.js (POCAS base URL). No Bearer in Authorization.

import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { API } from "../../utils/apiUrls";
import {
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  Save,
  RotateCcw,
  FileSpreadsheet,
} from "lucide-react";

const LOTTIE_LOADER = "/animations/loader.lottie";

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  const merchantId = localStorage.getItem("merchantId");
  if (!token) return null;
  return {
    Authorization: token,
    "Content-Type": "application/json",
    ...(merchantId ? { "x-merchant-id": merchantId } : {}),
  };
};

const formatColumnLabel = (key) => {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
};

export default function ReportConfiguration() {
  const [allowed, setAllowed] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [searchAvailable, setSearchAvailable] = useState("");
  const [searchSelected, setSearchSelected] = useState("");

  const fetchAllowed = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return [];
    const { data } = await axios.get(API.misColumnConfigAllowed(), { headers });
    return Array.isArray(data) ? data : [];
  }, []);

  const fetchConfig = useCallback(async () => {
    const headers = getAuthHeaders();
    if (!headers) return [];
    const { data } = await axios.get(API.misColumnConfigGet(), { headers });
    const names = data?.column_names ?? data?.columnNames;
    return Array.isArray(names) ? names : [];
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [allowedList, configList] = await Promise.all([fetchAllowed(), fetchConfig()]);
      setAllowed(allowedList);
      setSelected(configList.length > 0 ? configList : [...allowedList]);
    } catch (e) {
      const msg = e.response?.data?.message || e.message || "Failed to load configuration";
      toast.error(msg);
      setAllowed([]);
      setSelected([]);
    } finally {
      setLoading(false);
    }
  }, [fetchAllowed, fetchConfig]);

  useEffect(() => {
    load();
  }, [load]);

  const available = allowed.filter(
    (c) => !selected.includes(c) && formatColumnLabel(c).toLowerCase().includes(searchAvailable.toLowerCase())
  );
  const selectedFiltered = selected.filter((c) =>
    formatColumnLabel(c).toLowerCase().includes(searchSelected.toLowerCase())
  );

  const addColumn = (col) => setSelected((s) => [...s, col]);
  const addAll = () => setSelected((s) => [...s, ...available]);
  const removeColumn = (col) => setSelected((s) => s.filter((x) => x !== col));
  const removeAll = () => setSelected([]);

  const move = (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= selected.length) return;
    setSelected((s) => {
      const copy = [...s];
      [copy[index], copy[next]] = [copy[next], copy[index]];
      return copy;
    });
  };

  const handleSave = async () => {
    const headers = getAuthHeaders();
    if (!headers) {
      toast.error("Not authenticated");
      return;
    }
    setSaving(true);
    try {
      await axios.put(API.misColumnConfigSave(), selected, { headers });
      toast.success("Report configuration saved.");
    } catch (e) {
      const msg = e.response?.data?.message || e.message || "Failed to save";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const headers = getAuthHeaders();
    if (!headers) {
      toast.error("Not authenticated");
      return;
    }
    setResetting(true);
    try {
      await axios.delete(API.misColumnConfigReset(), { headers });
      setSelected([...allowed]);
      toast.success("Configuration reset to default.");
    } catch (e) {
      const msg = e.response?.data?.message || e.message || "Failed to reset";
      toast.error(msg);
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-8">
        <div className="w-24 h-24">
          <DotLottieReact src={LOTTIE_LOADER} loop autoplay style={{ width: "100%", height: "100%" }} />
        </div>
        <p className="mt-4 text-sm font-medium text-gray-600">Loading report configuration...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Page header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Report Configuration</h1>
          <p className="mt-1 text-sm text-gray-500">
            Choose and order columns for your MIS report. These settings apply when you generate reports.
          </p>
        </div>

        {/* Main card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200/80 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 bg-blue-700 text-white">
            <FileSpreadsheet className="w-5 h-5 text-blue-200 shrink-0" />
            <span className="text-base font-semibold">MIS Report Columns</span>
          </div>

          <div className="p-5 sm:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
              {/* Available columns */}
              <div className="flex flex-col border border-gray-200 rounded-lg bg-gray-50/50 min-h-[320px]">
                <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Available columns
                  </span>
                  <span className="text-xs text-gray-400">{available.length}</span>
                </div>
                <div className="p-2 border-b border-gray-100">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchAvailable}
                    onChange={(e) => setSearchAvailable(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400/30 outline-none bg-white"
                  />
                </div>
                <ul className="flex-1 overflow-y-auto p-2 space-y-0.5 max-h-64">
                  {available.length === 0 ? (
                    <li className="px-3 py-4 text-sm text-gray-400 text-center">No columns available</li>
                  ) : (
                    available.map((col) => (
                      <li key={col}>
                        <button
                          type="button"
                          onClick={() => addColumn(col)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-800 rounded-md border border-transparent hover:border-blue-200 transition-colors"
                        >
                          <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                          {formatColumnLabel(col)}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
                <div className="p-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={addAll}
                    disabled={available.length === 0}
                    className="w-full px-3 py-2 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-md border border-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add all
                  </button>
                </div>
              </div>

              {/* Center actions (visible on large screens) */}
              <div className="hidden lg:flex flex-col items-center justify-center gap-2 text-gray-400">
                <span className="text-xs font-medium text-gray-400">Selected columns define report order</span>
              </div>

              {/* Selected columns (order matters) */}
              <div className="flex flex-col border border-gray-200 rounded-lg bg-white min-h-[320px]">
                <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Selected columns (report order)
                  </span>
                  <span className="text-xs text-gray-400">{selected.length}</span>
                </div>
                <div className="p-2 border-b border-gray-100">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchSelected}
                    onChange={(e) => setSearchSelected(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:border-blue-400 focus:ring-1 focus:ring-blue-400/30 outline-none bg-white"
                  />
                </div>
                <ul className="flex-1 overflow-y-auto p-2 space-y-0.5 max-h-64">
                  {selectedFiltered.length === 0 ? (
                    <li className="px-3 py-4 text-sm text-gray-400 text-center">
                      {selected.length === 0 ? "No columns selected" : "No match in selected"}
                    </li>
                  ) : (
                    selectedFiltered.map((col) => {
                      const index = selected.indexOf(col);
                      return (
                        <li key={col} className="flex items-center gap-1 group">
                          <div className="flex flex-col shrink-0">
                            <button
                              type="button"
                              onClick={() => move(index, -1)}
                              disabled={index === 0}
                              className="p-0.5 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              aria-label="Move up"
                            >
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => move(index, 1)}
                              disabled={index === selected.length - 1}
                              className="p-0.5 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                              aria-label="Move down"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </button>
                          </div>
                          <span className="flex-1 min-w-0 px-2 py-1.5 text-sm text-gray-800 truncate">
                            {formatColumnLabel(col)}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeColumn(col)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="Remove"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
                <div className="p-2 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={removeAll}
                    disabled={selected.length === 0}
                    className="w-full px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-md border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-6 pt-4 border-t border-gray-100 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save configuration
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={resetting}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resetting ? (
                  <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4" />
                )}
                Reset to default
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
